import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useStats } from '../contexts/StatsContext';
import { mockData } from '../data/mock';
import { Star, ExternalLink } from 'lucide-react';

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const { googleReviews } = useStats();

  // Google Review Button Click Tracking (engagement only, not conversion)
  const handleReviewClick = () => {
    try {
      const consentStatus = localStorage.getItem('cookieConsent');
      const hasConsent = consentStatus === 'all' || consentStatus === 'accepted';
      
      if (typeof window.gtag !== 'function' || !hasConsent) return;

      window.gtag('event', 'google_review_click', {
        'event_category': 'engagement',
        'event_label': 'google_review_write'
      });
    } catch {
      // Fail silently
    }
  };

  return (
    <section className="section-padding bg-neutral-50">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            {language === 'de' ? 'Was unsere Teilnehmer sagen' : 'What our participants say'}
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-neutral-600 font-medium">
              {language === 'de' ? `Über ${googleReviews}+ Google Bewertungen` : `Over ${googleReviews}+ Google reviews`}
            </span>
          </div>
        </div>

        {/* Elfsight Google Reviews Widget */}
        <div className="mb-12">
          <div className="elfsight-app-2a8dbfbc-ec5b-4ee3-abfa-66c02b4b9da9" data-elfsight-app-lazy></div>
        </div>

        {/* Static Testimonials */}
        <div className="grid md:grid-cols-3 gap-6">
          {mockData.testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="testimonial-card bg-white p-6 rounded-2xl shadow-lg border border-neutral-100 card-hover"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-4 leading-relaxed">
                &ldquo;{testimonial.text[language]}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-neutral-900">{testimonial.name}</p>
                </div>
                <img
                  src="https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png"
                  alt="Google"
                  className="h-5 w-auto opacity-60"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Review Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          {/* Read all reviews link */}
          <a
            href="https://www.google.com/search?q=erste+hilfe+fokus+d%C3%BCsseldorf&oq=erste+hilfe+fokus"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors"
            data-testid="read-reviews-link"
          >
            <span>
              {language === 'de' ? 'Alle Bewertungen lesen' : 'Read all reviews'}
            </span>
            <ExternalLink className="w-4 h-4" />
          </a>

          {/* Review on Google CTA Button */}
          <a
            href="https://accounts.google.com/v3/signin/identifier?continue=https%3A%2F%2Fsearch.google.com%2Flocal%2Fwritereview%3Fplaceid%3DChIJb7JHLuvLuEcR-Fdd2K3XPx4&dsh=S774457165%3A1769854365329878&ifkv=AXbMIuBI2AHlEcHJW0O7aDzldObLdhdBF3ehBQ7-f4koE89MnV4jVX8byBZB3meBJdnWdVEx3aDTCw&flowName=GlifWebSignIn&flowEntry=ServiceLogin"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleReviewClick}
            className="inline-flex items-center gap-3 bg-white border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
            data-testid="write-review-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>
              {language === 'de' ? 'Jetzt auf Google bewerten' : 'Review us on Google'}
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
